import { generateBetItemId } from '../../utils/ticketHelpers';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { EventSnapshot, Market, Outcome } from '../../api/types';
import { SPORT_EMOJI } from './DesktopLeftSidebar';
import { formatOdds } from '../MatchDetailPage';
import { resolveMarketName, resolveOutcomeName } from '../../utils/marketTranslations';
import { useTranslation } from '../../i18n';
import { getEventName } from '../../utils/eventHelpers';

interface SearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  allEvents: EventSnapshot[];
  onNavigate?: (id: string) => void;
  onEventClick?: (ev: EventSnapshot) => void;
  onAddBet?: (ev: EventSnapshot, market: Market, outcome: Outcome, tipp: string) => void;
  isSelected?: (eventId: string, marketId: string, outcomeId: string, specifiers?: Record<string, string>) => boolean;
}

const SEARCH_TOP_COMPETITIONS = [
  { name: 'Premier League', country: 'England', flag: '🏴󠁧󠁢󠁥󠁮󠁧󠁿', count: 12 },
  { name: 'Coppa Italia', country: 'Italien', flag: '🇮🇹', count: 1 },
  { name: 'La Liga', country: 'Spanien', flag: '🇪🇸', count: 7 },
  { name: 'Ligue 1', country: 'Frankreich', flag: '🇫🇷', count: 12 },
  { name: 'Türkiye Kupasi', country: 'Türkei', flag: '🇹🇷', count: 1 },
];

export const SearchModal: React.FC<SearchModalProps> = ({
  isOpen, onClose, allEvents, onNavigate, onEventClick, onAddBet, isSelected
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchSportTab, setSearchSportTab] = useState<string>('Soccer');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } else {
      setSearchQuery('');
    }
  }, [isOpen]);

  const filteredMatches = useMemo(() => {
    if (!searchQuery || searchQuery.length < 2) return [];
    const q = searchQuery.toLowerCase();
    return allEvents.filter(ev =>
      ev.event.home?.name?.toLowerCase() || ''.includes(q) ||
      ev.event.away?.name?.toLowerCase() || ''.includes(q) ||
      ev.event.tournament_name.toLowerCase().includes(q) ||
      ev.event.category_name.toLowerCase().includes(q)
    );
  }, [allEvents, searchQuery]);

  const searchResultsGrouped = useMemo(() => {
    const grouped: Record<string, Record<string, EventSnapshot[]>> = {};
    filteredMatches.forEach((ev) => {
      const s = ev.event.sport_name;
      const t = ev.event.tournament_name;
      if (!grouped[s]) grouped[s] = {};
      if (!grouped[s][t]) grouped[s][t] = [];
      grouped[s][t].push(ev);
    });
    return grouped;
  }, [filteredMatches]);

  const foundSports = Object.keys(searchResultsGrouped);
  const effectiveSearchSport = foundSports.includes(searchSportTab) ? searchSportTab : (foundSports[0] || 'Soccer');

  const formatOdds = (val: string | number): string => {
    const n = parseFloat(String(val));
    if (isNaN(n) || n <= 1) return '-';
    return n.toFixed(2).replace('.', ',');
  };

  const find1x2 = (markets: Market[] | undefined) => {
    const m = markets?.find((mk) => mk.market_id === '1' && mk.status !== -1);
    if (!m) return null;
    const home = m.outcomes.find((o: Outcome) => o.outcome_id === '1');
    const draw = m.outcomes.find((o: Outcome) => o.outcome_id === '2' || o.name.toLowerCase() === 'draw' || o.name.toLowerCase() === 'x');
    const away = m.outcomes.find((o: Outcome) => o.outcome_id === '3');
    if (home && draw && away) return { home, draw, away, market: m };
    return null;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex justify-center items-start pt-[100px] bg-black/60 dark:bg-black/80 transition-opacity">
      {/* Overlay click to close */}
      <div className="absolute inset-0" onClick={onClose} />

      {/* Modal Container */}
      <div className="relative w-full max-w-[600px] bg-[#f2f2f2] dark:bg-[#1a1a1a] rounded-[8px] shadow-2xl overflow-hidden flex flex-col max-h-[calc(100vh-140px)] animate-slide-up">

        {/* Search Input Area */}
        <div className="bg-white dark:bg-[#252525] px-4 py-4 border-b border-[#e6e7e8] dark:border-[#333]">
          <div className="relative bg-[#f2f2f2] dark:bg-[#1a1a1a] rounded-[4px] flex items-center h-[48px] border border-[#e6e7e8] dark:border-[#333] focus-within:border-[#D41534] dark:focus-within:border-[#D41534] transition-colors">
            <div className="w-[48px] h-full flex items-center justify-center text-[#999999]">
              <svg className="w-[20px] h-[20px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <input
              ref={inputRef}
              type="text"
              placeholder="Team, Event oder Wettbewerb"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 bg-transparent text-[15px] font-medium text-[#212121] dark:text-white placeholder:text-[#999999] focus:outline-none h-full pr-3"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="w-[48px] h-full flex items-center justify-center text-[#999999] hover:text-[#454545] dark:hover:text-white"
              >
                <svg className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        {/* Search Results Area */}
        <div className="flex-1 overflow-y-auto scrollbar-hide">
          {searchQuery.length < 2 ? (
            <div className="p-4">
              <h3 className="text-[12px] font-bold text-[#727272] uppercase tracking-[0.5px] px-2 mb-3">Top-Wettbewerbe</h3>
              <div className="flex flex-col bg-white dark:bg-[#252525] rounded-[4px] shadow-[0_1px_3px_0_rgba(0,0,0,0.14)] overflow-hidden">
                {SEARCH_TOP_COMPETITIONS.map((comp, idx) => (
                  <button
                    key={comp.name}
                    onClick={() => {
                      if (onNavigate) {
                        onNavigate('category/' + encodeURIComponent(comp.country));
                        onClose();
                      }
                    }}
                    className={`flex items-center min-h-[56px] px-4 text-left hover:bg-[#f7f7f7] dark:hover:bg-[#333] transition-colors ${idx !== SEARCH_TOP_COMPETITIONS.length - 1 ? 'border-b border-[#e6e7e8] dark:border-[#333]' : ''}`}
                  >
                    <div className="w-[36px] h-[36px] rounded-full bg-[#f0f0f0] dark:bg-[#1a1a1a] flex items-center justify-center text-[18px] mr-3">
                      {comp.flag}
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-[14px] font-bold text-[#212121] dark:text-white truncate">{comp.name}</span>
                      <span className="text-[12px] text-[#727272] dark:text-[#a0a0a0] truncate">{comp.country}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <span className="text-[14px] font-bold text-[#999999]">{comp.count}</span>
                      <svg className="w-[16px] h-[16px] text-[#999999]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="flex flex-col h-full bg-white dark:bg-[#252525]">
              {foundSports.length > 0 ? (
                <>
                  <div className="flex bg-[#f2f2f2] dark:bg-[#1a1a1a] overflow-x-auto scrollbar-hide border-b border-[#e6e7e8] dark:border-[#333]">
                    {foundSports.map(s => (
                      <button
                        key={s}
                        onClick={() => setSearchSportTab(s)}
                        className={`flex-shrink-0 px-4 py-3 text-[13px] font-bold uppercase tracking-wider border-b-2 transition-colors ${effectiveSearchSport === s
                            ? 'border-[#D41534] text-[#D41534]'
                            : 'border-transparent text-[#727272] hover:text-[#212121] dark:hover:text-white'
                          }`}
                      >
                        {SPORT_EMOJI[s] || '🏅'} {s}
                      </button>
                    ))}
                  </div>

                  <div className="flex-1 overflow-y-auto pb-4">
                    {searchResultsGrouped[effectiveSearchSport] && Object.entries(searchResultsGrouped[effectiveSearchSport]).map(([tournament, events]) => (
                      <div key={tournament} className="mt-4 px-4">
                        <h4 className="text-[12px] font-bold text-[#212121] dark:text-white mb-2 ml-1">{tournament}</h4>
                        <div className="flex flex-col border border-[#e6e7e8] dark:border-[#333] rounded-[4px] overflow-hidden shadow-sm">
                          {events.map((ev, eIdx) => {
                            const odds3way = find1x2(ev.markets);
                            return (
                              <div key={ev.event_id} className={`flex flex-col p-3 hover:bg-[#f7f7f7] dark:hover:bg-[#333] transition-colors ${eIdx !== events.length - 1 ? 'border-b border-[#e6e7e8] dark:border-[#333]' : ''}`}>
                                <div className="flex items-start justify-between cursor-pointer" onClick={() => { onEventClick?.(ev); onClose(); }}>
                                  <div className="flex flex-col flex-1 min-w-0 pr-3">
                                    <span className="text-[13px] font-bold text-[#212121] dark:text-white truncate">{ev.event.home?.name}</span>
                                    <span className="text-[13px] font-bold text-[#212121] dark:text-white truncate">{ev.event.away?.name}</span>
                                  </div>
                                </div>
                                {odds3way && onAddBet && isSelected && (
                                  <div className="flex items-center gap-1 mt-3">
                                    {[
                                      { label: '1', outcome: odds3way.home, tipp: '1' },
                                      { label: 'X', outcome: odds3way.draw, tipp: 'X' },
                                      { label: '2', outcome: odds3way.away, tipp: '2' },
                                    ].map(({ label, outcome, tipp }) => {
                                      const sel = isSelected(ev.event_id, odds3way.market.market_id, outcome.outcome_id, odds3way.market.specifiers);
                                      return (
                                        <button
                                          key={label}
                                          onClick={(e) => { e.stopPropagation(); onAddBet(ev, odds3way.market, outcome, tipp); }}
                                          className={`flex-1 h-[32px] rounded-[4px] text-[13px] font-bold flex items-center justify-center transition-colors border ${sel
                                              ? 'bg-gray-900 text-white border-gray-900'
                                              : 'bg-white dark:bg-[#252525] border-[#d1d5da] dark:border-[#444] text-[#212121] dark:text-white hover:bg-[#f2f2f2] dark:hover:bg-[#333]'
                                            }`}
                                        >
                                          <span className="text-[#999999] mr-1 text-[11px]">{label}</span>
                                          {formatOdds(outcome.odds)}
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-[200px] text-[#727272] text-[14px]">
                  Keine Ergebnisse gefunden für "{searchQuery}"
                </div>
              )}
            </div>
          )}
        </div>

      </div>

      {/* Close button top right */}
      <button onClick={onClose} className="absolute top-4 right-4 md:top-8 md:right-8 p-2 rounded-full bg-black/40 text-white hover:bg-black/60 transition">
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};
